import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';

const GideonHeader = ({
  showMenuButton,
  onToggleSidebar,
  showBackButton = true,
  conversationCount = 0,
  onShowConversations,
  onNewConversation
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  return (
    <div style={{
      height: '64px',
      flexShrink: 0,
      minWidth: 0,
      background: 'rgba(10, 13, 20, 0.95)',
      backdropFilter: 'blur(30px)',
      WebkitBackdropFilter: 'blur(30px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '0 12px' : '0 20px',
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.4), 0 0 40px rgba(0, 225, 196, 0.05)',
      position: 'sticky',
      top: 0,
      zIndex: 50
    }}>
      {/* Left Side — may shrink; the title ellipsizes rather than pushing
          the right-side controls off screen */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '10px' : '16px',
        flex: 1,
        minWidth: 0
      }}>
        {/* Back Button */}
        {showBackButton && (
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            style={{
              width: '40px',
              height: '40px',
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '8px',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '300'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            ←
          </button>
        )}

        {/* Menu Button (mobile) */}
        {showMenuButton && (
          <button
            onClick={onToggleSidebar}
            aria-label="Open menu"
            style={{
              width: '40px',
              height: '40px',
              background: 'rgba(0, 225, 196, 0.1)',
              border: '1px solid rgba(0, 225, 196, 0.3)',
              borderRadius: '8px',
              color: '#00e1c4',
              fontSize: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(0, 225, 196, 0.2)';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(0, 225, 196, 0.1)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            ☰
          </button>
        )}

        {/* Brand Text */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          minWidth: 0
        }}>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? '1rem' : '1.25rem',
            fontWeight: '700',
            color: '#ffffff',
            letterSpacing: '0.02em',
            lineHeight: '1.2',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%'
          }}>
            GIDEON
          </h1>
          <p style={{
            margin: 0,
            fontSize: isMobile ? '0.6875rem' : '0.75rem',
            fontWeight: '500',
            color: '#00e1c4',
            letterSpacing: '0.03em',
            lineHeight: '1.2',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%'
          }}>
            {isMobile ? 'AI Guide' : 'AI Financial Guide'}
          </p>
        </div>
      </div>

      {/* Right Side - Conversation Controls and Verified Badge */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? '8px' : '12px',
        flexShrink: 0
      }}>
        {/* Conversation Management Buttons */}
        {onShowConversations && onNewConversation && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {/* Conversations Button */}
            <button
              onClick={onShowConversations}
              aria-label="Show conversations"
              style={{
                height: '36px',
                padding: '0 12px',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.875rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <span style={{ fontSize: '1rem' }}>💬</span>
              <span className="conversation-label" style={{
                display: window.innerWidth > 768 ? 'inline' : 'none'
              }}>
                {conversationCount}
              </span>
            </button>

            {/* New Chat Button */}
            <button
              onClick={onNewConversation}
              aria-label="New conversation"
              style={{
                height: '36px',
                padding: '0 12px',
                background: 'linear-gradient(135deg, #00e1c4, #5fffe6)',
                border: 'none',
                borderRadius: '8px',
                color: '#0a0d14',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 225, 196, 0.3)',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 225, 196, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 225, 196, 0.3)';
              }}
            >
              <span style={{ fontSize: '1.1rem', fontWeight: '800', lineHeight: 1 }}>+</span>
              <span className="new-chat-label">New</span>
            </button>
          </div>
        )}

        {/* Verified Badge — icon-only on phones, where the full pill
            overflowed the 390px header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: isMobile ? '3px' : '6px 12px',
          background: 'rgba(0, 225, 196, 0.1)',
          border: '1px solid rgba(0, 225, 196, 0.3)',
          borderRadius: '20px',
          boxShadow: '0 0 12px rgba(0, 225, 196, 0.15)'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            background: 'linear-gradient(135deg, #00e1c4, #5fffe6)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            color: '#0a0d14',
            fontWeight: '700',
            boxShadow: '0 2px 8px rgba(0, 225, 196, 0.3)'
          }}>
            ✓
          </div>
          {!isMobile && (
            <span style={{
              fontSize: '0.6875rem',
              fontWeight: '600',
              color: '#00e1c4',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Verified
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GideonHeader;
